import { CaretRight, CheckCircle } from "@phosphor-icons/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { useAuth } from "../../context/AuthContext";
import useLinkedAccounts from "../../hooks/useLinkedAccounts";
import { useAccountSetupMetric } from "../../components/CompleteSetupTaskList";
import useCreateAlertAccount from "../../hooks/useCreateAlertAccount";

type StatusKind = "complete" | "action" | "review";

const isTruthy = (value: unknown): boolean => {
  if (value === true) return true;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") return value.length > 0 && value !== "0";
  return !!value;
};

function StatusChip({ kind }: { kind: StatusKind }) {
  const styles: Record<StatusKind, string> = {
    complete: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200",
    action: "bg-amber-50 text-amber-900 ring-1 ring-amber-200",
    review: "bg-sky-50 text-sky-900 ring-1 ring-sky-200",
  };
  const label =
    kind === "complete"
      ? "Complete"
      : kind === "review"
      ? "In review"
      : "Action needed";
  return (
    <span
      className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${styles[kind]}`}
    >
      {label}
    </span>
  );
}

const rowInteractive =
  "flex items-stretch gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:bg-gray-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-deepBlue/25 w-full";
const rowStatic =
  "flex items-stretch gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left";

function ComplianceTaskRow(props: {
  title: string;
  description: string;
  status: StatusKind;
  to?: string;
  onClick?: () => void;
  interactive: boolean;
}) {
  const { title, description, status, to, onClick, interactive } = props;
  const body = (
    <>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-semibold text-gray-900">{title}</span>
          <StatusChip kind={status} />
        </div>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>
      {status === "complete" ? (
        <CheckCircle
          weight="fill"
          className="text-visaro-orange shrink-0 mt-0.5"
          size={24}
          aria-hidden
        />
      ) : (
        <CaretRight
          size={22}
          className="text-visaro-orange shrink-0 mt-1"
          aria-hidden
        />
      )}
    </>
  );

  if (interactive && to) {
    return (
      <Link to={to} className={rowInteractive}>
        {body}
      </Link>
    );
  }
  if (interactive && onClick) {
    return (
      <button type="button" onClick={onClick} className={rowInteractive}>
        {body}
      </button>
    );
  }
  return <div className={rowStatic}>{body}</div>;
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-2 first:mt-0">
      {children}
    </h2>
  );
}

const Compliance = () => {
  const { user, refetch } = useAuth();
  const { accounts } = useLinkedAccounts();
  const location = useLocation();
  const navigate = useNavigate();
  const metric = useAccountSetupMetric();
  const international = metric.international;

  const hasLinkedAccounts = (accounts?.data?.length || 0) > 0;
//   const kycLevel = parseInt(user?.wallet?.tier ?? user?.compliance?.kyc_level ?? "1", 10);

  const kycLevel = parseInt(user?.compliance?.kyc_level ?? user?.wallet?.tier ?? "1", 10);

  const idComplete = !!(
    user?.compliance?.verified_regulatory_id ||
    user?.compliance?.compliance_image_1
  );

//   const utilityStatus = useMemo((): StatusKind => {
//     const s = user?.compliance?.utility_status_1;
//     const hasFile = !!user?.compliance?.utility_image_1;
//     if (!s && !hasFile) return "action";
//     if (String(s) === "IN_REVIEW") return "review";
//     if (String(s) === "APPROVED" || hasFile) return "complete";
//     if (String(s) === "REJECTED") return "action";
//     return "review";
//   }, [user?.compliance]);

  const complianceProgress = useMemo(() => {
    const bvn = metric.checks.bvn;
    const nin = metric.checks.nin;
    const emp = metric.checks.employmentDetails;
    const util = metric.checks.utility_bill;
    const id = idComplete;
    const items = metric.international
      ? [metric.checks.address, metric.checks.passport, emp, util, id]
      : [bvn, nin, emp, util, id];
    return {
      done: items.filter(Boolean).length,
      total: items.length,
    };
  }, [metric.checks, metric.international, idComplete]);

  const ninVerified = isTruthy(user?.compliance?.nin_verified);
  const bvnVerified = isTruthy(user?.compliance?.bvn_verified);
  const ninStatus: StatusKind = ninVerified ? "complete" : "action";
  const bvnStatus: StatusKind = bvnVerified ? "complete" : "action";
  const empDone = metric.checks.employmentDetails;
  const empStatus: StatusKind = empDone ? "complete" : "action";
//   const idStatus: StatusKind = idComplete ? "complete" : "action";

  // Ensure the user's Alert account is created once tier-2 identity requirements
  // are complete but the account is still missing. createAccount is idempotent
  // on the backend, so this is a safe fire-and-forget call.
  const { createAlertAccount } = useCreateAlertAccount({
    onSuccess() {
      refetch();
    },
  });
  const alertAccountRequested = useRef(false);

  useEffect(() => {
    if (
      !alertAccountRequested.current &&
      ninVerified &&
      bvnVerified &&
      empDone &&
      user?.account_alert !== 1
    ) {
      alertAccountRequested.current = true;
      createAlertAccount();
    }
  }, [ninVerified, bvnVerified, empDone, user?.account_alert, createAlertAccount]);

  return (
		<div className="bg-gray-50 px-5 md:px-10 py-6 lg:py-8 flex items-center justify-center">
			<div className="flex flex-col gap-6 max-w-3xl w-full">
				<header className="flex flex-col gap-1">
					<h1 className="text-2xl font-bold text-gray-900">
						Compliance
					</h1>
					<p className="text-sm text-gray-600">
						Complete verification to unlock higher limits, loans,
						and regulated services. Tasks are grouped by category.
					</p>
					{!metric.isLoading && (
						<div className="mt-3 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
							<span className="text-sm font-medium text-gray-700">
								Core verification: {complianceProgress.done} /{" "}
								{complianceProgress.total} complete
							</span>
							<div className="h-2 flex-1 max-w-xs rounded-full bg-gray-200 overflow-hidden">
								<div
									className="h-full rounded-full bg-visaro-orange transition-all"
									style={{
										width: `${
											complianceProgress.total
												? (complianceProgress.done /
														complianceProgress.total) *
													100
												: 0
										}%`,
									}}
								/>
							</div>
						</div>
					)}
				</header>

				<div className="lg:bg-white lg:rounded-xl w-full lg:shadow-sm flex flex-col gap-4 lg:p-6">
					<SectionTitle>Identity &amp; KYC</SectionTitle>
					<ComplianceTaskRow
						title="KYC tiers & limits"
						description={
							kycLevel < 3
								? `See daily and loan limits by tier. Upgrade toward Tier ${kycLevel + 1}.`
								: `You're on Tier ${kycLevel} — maximum tier.`
						}
						status={kycLevel >= 3 ? "complete" : "action"}
						to="kyc"
						interactive
					/>

					{!international && <ComplianceTaskRow
						title="NIN verification"
						description={
							ninVerified
								? "Your National Identification Number has been verified."
								: "Verify your National Identification Number."
						}
						status={ninStatus}
						interactive={!ninVerified}
						onClick={() =>
							navigate("/verify-nin", {
								state: {
									background: location,
									name: "verify-nin",
								},
							})
						}
					/>}

					{international && (
						<>
							<ComplianceTaskRow
								title="Home address"
								description="Add your residential address and postcode."
								status={metric.checks.address ? "complete" : "action"}
								to="home-address"
								interactive={true}
							/>
							<ComplianceTaskRow
								title="International passport"
								description="Submit a passport for international-customer KYC."
								status={metric.checks.passport ? "complete" : "action"}
								to="travel-document"
								interactive={true}
							/>
						</>
					)}

					{!international && <ComplianceTaskRow
						title="BVN verification"
						description={
							bvnVerified
								? "Your Bank Verification Number has been verified."
								: "Verify your Bank Verification Number."
						}
						status={bvnStatus}
						interactive={!bvnVerified}
						onClick={() =>
							navigate("/verify-bvn", {
								state: {
									background: location,
									name: "verify-bvn",
								},
							})
						}
					/>}

					{/* <ComplianceTaskRow
						title="ID verification"
						description={
							idComplete
								? "Your ID has been submitted for verification."
								: "Verify your ID with your Driver's License or Permanent Voters Card (PVC)."
						}
						status={idStatus}
						to="id-verification"
						interactive={!idComplete}
					/> */}

					{/* {kycLevel < 3 && (
						<Link
							to="upgrade-tier-3"
							className="flex items-center border border-visaro-orange p-4 gap-2 rounded-md hover:bg-orange-50 transition-all cursor-pointer text-left"
						>
							<div className="flex-1">
								<div className="text-lg font-medium text-visaro-orange">
									Complete KYC Steps
								</div>
								<div className="opacity-70">
									Submit your ID, utility bill, and photo to
									upgrade your account tier
								</div>
							</div>
							<CaretRight
								size={22}
								className="text-visaro-orange"
							/>
						</Link>
					)} */}

					<SectionTitle>Employment</SectionTitle>

					{/* <ComplianceTaskRow
						title="Upload utility bill"
						description={
							user?.compliance?.utility_status_1
								? "Your utility bill has been submitted."
								: "Upload a clear photo of a recent utility bill (electricity, water, or gas)."
						}
						status={utilityStatus}
						to="utility-bill"
						interactive={utilityStatus !== "complete"}
					/> */}

					<ComplianceTaskRow
						title="Employment details"
						description={
							user?.compliance?.employment_status
								? `Current status: ${user.compliance.employment_status}.`
								: "Add your employment status and related details."
						}
						status={empStatus}
						to="employment-details"
						interactive={!empDone}
					/>

					<SectionTitle>Banking</SectionTitle>

					<ComplianceTaskRow
						title="Bank details"
						description={
							user?.compliance?.bank_account_number
								? "Your bank account number has been verified."
								: "Verify your bank account number."
						}
						status={
							user?.compliance?.bank_account_number
								? "complete"
								: "action"
						}
						interactive={!user?.compliance?.bank_account_number}
						onClick={() =>
							navigate("/verify-bank", {
								state: {
									background: location,
									name: "verify-bank",
								},
							})
						}
					/>

					<ComplianceTaskRow
						title="Link bank account"
						description={
							hasLinkedAccounts
								? "A bank account is linked for statements and verification."
								: "Link a bank account to generate bank statements where required."
						}
						status={hasLinkedAccounts ? "complete" : "action"}
						to="bank-accounts"
						interactive={true}
					/>
				</div>
			</div>
		</div>
  );
};

export default Compliance;
